// ─── The sign-in gate ────────────────────────────────────────────────────────
//
// Deliberately plain: one email, one password, one button, and no "forgot
// password" link — there is nowhere for it to go yet, and a link that does
// nothing is worse than no link. Resetting a password is a command on the
// machine, which each product says in its own words through `resetHint` so
// somebody locked out knows what to ask for rather than guessing.
//
// Extracted from Crafting Comb on 2026-09-15 for the Memorial Hall committee
// app. The `submit` function is passed in rather than an API module imported,
// so this component knows nothing about either product's routes.

import { useState } from "react";

export function SignInForm({ organisationName, heading, submit, onSignedIn, resetHint }: {
  /** The eyebrow above the heading — what the organisation is called. */
  organisationName: string;
  /** What this gate is in front of: "Salon diary", "Committee calendar". */
  heading: string;
  /** Do the sign-in. Throws with the SERVER'S own wording on failure, which is
   *  shown unchanged: it says the same thing for a wrong password as for an
   *  unknown email on purpose, and something different when an account is
   *  locked. Both are worth passing through. */
  submit: (email: string, password: string) => Promise<unknown>;
  onSignedIn: () => void;
  resetHint?: React.ReactNode;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await submit(email, password);
      onSignedIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not sign in.");
      setPassword("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="sign-in"
      style={{
        display: "flex", flexDirection: "column", gap: 12,
        maxWidth: 380, margin: "8vh auto 0",
        background: "var(--nl-surface)", border: "1px solid var(--nl-stroke)",
        borderRadius: "var(--nl-radius-card)", padding: 20,
      }}
    >
      <div>
        <div style={{
          fontSize: "0.72rem", letterSpacing: "0.12em",
          textTransform: "uppercase", color: "var(--nl-ink-faint)",
        }}>
          {organisationName}
        </div>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.3rem", fontWeight: 600 }}>{heading}</h1>
      </div>

      {error && (
        <p
          role="alert"
          data-testid="sign-in-error"
          style={{ margin: 0, color: "var(--nl-status-declined)", fontSize: "0.9rem" }}
        >
          {error}
        </p>
      )}

      <label style={label}>
        Email
        <input
          type="email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          autoComplete="username"
          // Focused on load: this screen exists to be typed into.
          autoFocus
          required
          style={field}
        />
      </label>

      <label style={label}>
        Password
        <input
          type="password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          autoComplete="current-password"
          required
          style={field}
        />
      </label>

      <button
        type="submit"
        disabled={busy}
        style={{ ...primary, opacity: busy ? 0.6 : 1 }}
        data-testid="sign-in-submit"
      >
        {busy ? "Signing in…" : "Sign in"}
      </button>

      {resetHint && (
        <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--nl-ink-faint)" }}>
          {resetHint}
        </p>
      )}
    </form>
  );
}

const label: React.CSSProperties = {
  display: "flex", flexDirection: "column", gap: 4,
  fontSize: "0.8rem", color: "var(--nl-ink-dim)",
};

const field: React.CSSProperties = {
  minHeight: "var(--nl-hit-min)",
  borderRadius: "var(--nl-radius-control)",
  border: "1px solid var(--nl-stroke)",
  background: "var(--nl-surface-raised)",
  color: "var(--nl-ink)",
  padding: "0 12px", font: "inherit", width: "100%",
};

const primary: React.CSSProperties = {
  minHeight: "var(--nl-hit-min)", padding: "0 18px", fontWeight: 600,
  borderRadius: "var(--nl-radius-control)", border: "1px solid var(--nl-stroke)",
  background: "var(--nl-state-open)", color: "var(--nl-state-open-ink)",
  fontFamily: "inherit", fontSize: "inherit", cursor: "pointer",
};
